import React from "react"

import {
    Chip,
    CircularProgress,
    FormControl,
    FormHelperText,
    InputLabel,
    Link, makeStyles,
    Select,
    TextField, Typography
} from "@material-ui/core"
import { LocalizedStrict } from "../../Modules/Localization/Components/Localized"
import { Autocomplete, AutocompleteProps } from "@material-ui/lab"
import AsteriskComponent from "../Layout/Asterisk"
import { useField } from "formik"
import { useLocalization } from "@fluent/react"

type StrictOmit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>

const useStyles = makeStyles((theme) => ({
    autocomplete: {
        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"]': {
            paddingTop: '9px',
            paddingBottom: '9px'
        },

        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] .MuiAutocomplete-input': {
            padding: '9.5px 4px'
        }
    },
    mb_1: {
        marginBottom: theme.spacing(1),
    },
    placeholder: {
        color: theme.palette.text.disabled,
        fontStyle: 'normal',
    }
}))

export type ApiQueryAutocompleteProps<T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
    > = StrictOmit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    keyof AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>> &
    Readonly<{
        label?: string
        fullWidth?: boolean
        isFloatingLabel?: boolean
        required?: boolean
        multiple?: boolean
        options: Array<{ label: string, value: string }>
        placeholder?: string
        isLoading: boolean
        isError: boolean
        onRetry?: () => void
    }>

export function ApiQueryAutocomplete({
          name,
          label,
          fullWidth,
          isFloatingLabel,
          required,
          multiple,
          isLoading,
          placeholder,
          options,
          isError,
          onRetry,
          ...rest
      }: any) {
    const classes = useStyles()
    const { l10n } = useLocalization()

    const [_, meta] = useField(name)

    return (
        <>
            {isError && (
                <FormControl error={true} fullWidth={fullWidth}>
                    <InputLabel id="api-query-autocomplete-select-label">{label}</InputLabel>
                    <Select
                        labelId="api-query-autocomplete-select-label"
                        label={label}
                        fullWidth={fullWidth}
                        readOnly={true}
                    />
                    <LocalizedStrict
                        id="api-query-autocomplete-fetch-error-message"
                        elems={{
                            retryLink: onRetry ? (
                                <Link onClick={onRetry} />
                            ) : (
                                <span style={{ display: "none" }} />
                            )
                        }}
                    >
                        <FormHelperText>
                            Error loading data. <Link>Retry</Link>
                        </FormHelperText>
                    </LocalizedStrict>
                </FormControl>
            )}

            {!isError ? <>
                {label && !isFloatingLabel ? (
                    <Typography variant="body1" className={classes.mb_1}>
                        {label}
                        <AsteriskComponent required={required || false} />
                    </Typography>
                ) : null}
                <FormControl fullWidth variant="outlined" error={meta.touched && Boolean(meta.error)}>
                        <Autocomplete
                            {...rest}
                            multiple={multiple}
                            options={options}
                            fullWidth={fullWidth}
                            className={classes.autocomplete}
                            loading={isLoading}
                            loadingText={l10n.getString('api-query-autocomplete')}
                            renderOption={(option: { label: string, value: string }) => (
                                <Typography>{option.label}</Typography>
                            )}
                            getOptionSelected={(option: {label: string, value: string}, value) => option.value === value.value}
                            renderTags={(value, getTagProps) =>
                                value.map((option: { label: string, value: string }, index) => (
                                    <Chip
                                        label={option.label}
                                        {...getTagProps({ index })}
                                        key={option.value}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label={label && isFloatingLabel ? label : ''}
                                    placeholder={placeholder || ''}
                                    error={meta.touched && Boolean(meta.error)}
                                    helperText={meta.touched ? meta.error : undefined}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                </FormControl>
            </> : null}
        </>
    )
}
